import { useState, useEffect, useCallback, useRef } from "react";
import { http, BACKEND } from "@/lib/http";

const API_BASE = `${BACKEND}/api`;

export const api = {
  getAgents: (params) => http.get("/agents", { params }),
  getAgentHierarchy: () => http.get("/agents/hierarchy"),
  getAgent: (id) => http.get(`/agents/${id}`),
  createAgent: (data) => http.post("/agents", data),
  updateAgent: (id, data) => http.put(`/agents/${id}`, data),

  getTasks: (params) => http.get("/tasks", { params }),
  createTask: (data) => http.post("/tasks", data),
  updateTask: (id, data) => http.put(`/tasks/${id}`, data),
  bulkAction: (data) => http.post("/tasks/bulk-action", data),
  smartAssign: (data) => http.post("/tasks/smart-assign", data),

  getTemplates: () => http.get("/templates"),
  createTemplate: (data) => http.post("/templates", data),

  getTeams: () => http.get("/teams"),
  createTeam: (data) => http.post("/teams", data),

  getMetricsOverview: () => http.get("/metrics/overview"),
  getPerformanceMetrics: () => http.get("/metrics/performance"),
  getHeatmapData: () => http.get("/metrics/heatmap"),
  getTaskDistribution: () => http.get("/metrics/task-distribution"),
  getPredictions: () => http.get("/metrics/predictions"),

  getLeaderboard: () => http.get("/leaderboard"),
  getAuditLogs: (params) => http.get("/audit-logs", { params }),
  getActivityFeed: (params) => http.get("/activity-feed", { params }),

  getHealth: () => http.get("/health"),
  seedData: () => http.post("/seed"),

  getSkills: (params) => http.get("/skills", { params }),
  toggleSkill: (id) => http.post(`/skills/${id}/toggle`),
  getCustomKpis: () => http.get("/custom-kpis"),
  createCustomKpi: (data) => http.post("/custom-kpis", data),
  deleteCustomKpi: (id) => http.delete(`/custom-kpis/${id}`),
};

export function useSSE(onEvent) {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    const es = new EventSource(`${API_BASE}/sse/stream`);
    es.onmessage = (evt) => {
      try {
        cbRef.current(JSON.parse(evt.data));
      } catch (e) {
        console.error("SSE parse error", e);
      }
    };
    es.onerror = () => {};
    return () => es.close();
  }, []);
}

export function useFetch(fetchFn) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fetchFn);
  fnRef.current = fetchFn;

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fnRef.current();
      setData(res.data);
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, loading, refetch };
}
